import type { VehicleStatusVehicleCapabilities } from '@backoffice-frontend/graphql'
import {
  SdsMode,
  VehicleStatusDoorPosition,
  VehicleStatusDoorState,
  VehicleStatusSdsMode,
} from '@backoffice-frontend/graphql'

export type ConnectivitySignal =
  | { connected: boolean }
  | { connectivityState: boolean }
  | { value: boolean }

type DoorStateSignal = {
  position: VehicleStatusDoorPosition
  state: VehicleStatusDoorState
}

type DoorStates = { states: readonly DoorStateSignal[] }

export const isConnected = (
  signal: ConnectivitySignal | null | undefined,
): boolean | undefined => {
  if (!signal) return undefined
  if ('connected' in signal) return signal.connected
  if ('connectivityState' in signal) return signal.connectivityState

  return signal.value
}

// The new API reports five door states, while the rest of the frontend is built
// around a single "is the door open" boolean. Transitional states keep the value
// they are moving away from, so the boolean only flips once the door has
// actually arrived: a closing door still counts as open, an opening one as
// closed. AJAR means the door is stuck and can only be resolved by a close
// request, so it counts as open too.
const isOpenByDoorState: Record<VehicleStatusDoorState, boolean> = {
  [VehicleStatusDoorState.Open]: true,
  [VehicleStatusDoorState.Closing]: true,
  [VehicleStatusDoorState.Ajar]: true,
  [VehicleStatusDoorState.Closed]: false,
  [VehicleStatusDoorState.Opening]: false,
}

export const isDoorStateOpen = (state: VehicleStatusDoorState): boolean =>
  isOpenByDoorState[state]

export const isDoorOpen = (
  doorState: DoorStates | null | undefined,
  position: VehicleStatusDoorPosition,
): boolean | undefined => {
  const signal = doorState?.states.find(state => state.position === position)

  return signal ? isDoorStateOpen(signal.state) : undefined
}

export const isRightDoorOpen = (doorState: DoorStates | null | undefined) =>
  isDoorOpen(doorState, VehicleStatusDoorPosition.PassengerDoorRight)

export const isAnyDoorOpen = (
  doorState: DoorStates | null | undefined,
): boolean | undefined => {
  if (!doorState?.states.length) return undefined

  return doorState.states.some(signal => isDoorStateOpen(signal.state))
}

// The AD vehicle simulator still writes through the old fleet-control mutation,
// whose input takes the legacy SdsMode enum.
const legacySdsModes: Record<VehicleStatusSdsMode, SdsMode> = {
  [VehicleStatusSdsMode.Disengaged]: SdsMode.Disengaged,
  [VehicleStatusSdsMode.Engaged]: SdsMode.Engaged,
  [VehicleStatusSdsMode.Fault]: SdsMode.Fault,
}

export const toLegacySdsMode = (
  mode: VehicleStatusSdsMode | null | undefined,
): SdsMode | undefined => (mode ? legacySdsModes[mode] : undefined)

export type TelemetrySignalName = keyof Omit<
  VehicleStatusVehicleCapabilities,
  '__typename'
>

export const supportsSignal = (
  capabilities:
    | Partial<Pick<VehicleStatusVehicleCapabilities, TelemetrySignalName>>
    | null
    | undefined,
  signal: TelemetrySignalName,
): boolean => capabilities?.[signal] !== false

export const toSpeedKmh = (metersPerSecond: number) => metersPerSecond * 3.6

export const parseTelemetryUpdatedAt = (
  updatedAt: string | null | undefined,
): Date | undefined => {
  if (!updatedAt) return undefined

  const date = new Date(updatedAt)

  return Number.isNaN(date.valueOf()) ? undefined : date
}
